import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../contexts/AuthContext';

interface QuoteForMe {
  id: string;
  quote_text: string;
  author_name: string;
  author_title: string | null;
  photo_url: string | null;
}

const initials = (name: string) =>
  name.split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase().substring(0, 2);

const todayKey = () => new Date().toISOString().slice(0, 10); // YYYY-MM-DD

// Shown once per calendar day, right after login — curated by the super-admin
// in Cockpit > Login Quotes.
const WelcomeQuoteModal: React.FC = () => {
  const { user, token } = useAuth();
  const [quote, setQuote] = useState<QuoteForMe | null>(null);

  useEffect(() => {
    if (!user || !token) return;

    const storageKey = `login_quote_shown_${user.id}`;
    if (localStorage.getItem(storageKey) === todayKey()) return;

    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

    (async () => {
      try {
        const response = await fetch(`${baseUrl}/api/v1/login-quotes/for-me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (response.ok && data.success && data.data) {
          setQuote(data.data);
        }
      } catch {
        // Silently skip — a missing quote should never block the dashboard.
      } finally {
        localStorage.setItem(storageKey, todayKey());
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, token]);

  return (
    <AnimatePresence>
      {quote && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="w-full max-w-md rounded-2xl p-7 relative overflow-hidden text-white shadow-2xl"
            style={{ background: 'radial-gradient(120% 140% at 0% 0%, rgba(185,139,46,0.35), transparent 55%), linear-gradient(165deg, #1b2648, #10182f 70%)' }}
          >
            <button
              onClick={() => setQuote(null)}
              className="absolute top-3.5 right-3.5 w-7 h-7 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Close"
            >
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>

            <span
              className="absolute -top-5 left-4 text-[120px] leading-none"
              style={{ color: 'rgba(217,178,92,0.28)', fontFamily: 'Georgia, serif' }}
            >
              &ldquo;
            </span>

            <p className="relative italic text-[19px] leading-relaxed mb-5 mt-2">
              &ldquo;{quote.quote_text}&rdquo;
            </p>

            <div className="h-px w-full mb-5" style={{ background: 'linear-gradient(90deg, rgba(217,178,92,0.6), rgba(217,178,92,0))' }} />

            <div className="flex items-center gap-3">
              {quote.photo_url ? (
                <img src={quote.photo_url} alt={quote.author_name} className="w-11 h-11 rounded-full object-cover" />
              ) : (
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-[13px] font-bold" style={{ background: 'linear-gradient(145deg, #d9b25c, #6b7ea8)' }}>
                  {initials(quote.author_name)}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-bold truncate">{quote.author_name}</p>
                {quote.author_title && (
                  <p className="text-xs truncate" style={{ color: 'rgba(242,239,230,0.65)' }}>{quote.author_title}</p>
                )}
              </div>
            </div>

            <button
              onClick={() => setQuote(null)}
              className="mt-6 w-full py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-white/10 hover:bg-white/20 transition-colors"
            >
              Continue to Dashboard
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default WelcomeQuoteModal;
